import axios from "axios";

const JSON_HEADERS = {
  "Content-Type": "application/json",
};

const getLambdaApiUrl = () => process.env.REACT_APP_LAMBDA_API_URL;

const postUploadApi = async (endpoint, requestData) => {
  const webApiUrl = getLambdaApiUrl();
  const apiUrl = `${webApiUrl}/${endpoint}`;

  return axios.post(apiUrl, requestData, {
    headers: JSON_HEADERS,
    validateStatus: () => true,
  });
};

export const isApiSuccess = (response) => {
  return response?.status === 200 && Number(response?.data?.code) === 200;
};

const SENSITIVE_DEBUG_KEYS = new Set(["uploadUrl", "viewUrl"]);

const sanitizeApiDebugData = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeApiDebugData(item));
  }

  if (!data || typeof data !== "object") {
    return data;
  }

  return Object.entries(data).reduce((sanitizedData, [key, value]) => {
    sanitizedData[key] = SENSITIVE_DEBUG_KEYS.has(key)
      ? "[masked]"
      : sanitizeApiDebugData(value);

    return sanitizedData;
  }, {});
};

const stringifyApiData = (data) => {
  if (data === undefined) {
    return "undefined";
  }

  if (data === null) {
    return "null";
  }

  if (typeof data === "string") {
    return data;
  }

  try {
    return JSON.stringify(sanitizeApiDebugData(data));
  } catch (error) {
    return String(data);
  }
};

export const buildApiDebugSummary = (response) => {
  return {
    status: response?.status,
    code: response?.data?.code,
    message: response?.data?.message,
    dataType: typeof response?.data,
    dataSummary: stringifyApiData(response?.data),
  };
};

export const extractApiErrorMessage = (response, fallbackMessage) => {
  const responseStatus = response?.status;
  const responseCode = response?.data?.code;
  const responseMessage = response?.data?.message;
  const lambdaErrorMessage = response?.data?.errorMessage;
  const lambdaErrorType = response?.data?.errorType;
  const responseDataSummary = stringifyApiData(response?.data);

  if (responseMessage) {
    return responseCode !== undefined
      ? `${responseMessage} (code: ${responseCode})`
      : responseMessage;
  }

  if (lambdaErrorMessage) {
    return lambdaErrorType
      ? `${lambdaErrorMessage} (${lambdaErrorType})`
      : lambdaErrorMessage;
  }

  if (responseCode !== undefined) {
    return `API Error (code: ${responseCode})`;
  }

  if (responseStatus !== undefined || response?.data !== undefined) {
    return `API Error (status: ${responseStatus}, data: ${responseDataSummary})`;
  }

  return fallbackMessage;
};

export const buildUploadFormRequest = ({
  userEmail,
  orderDate,
  shipDate,
  distributor,
  inputPattern,
  uploadPath,
}) => {
  const webApiUrl = getLambdaApiUrl();

  return {
    momRegisterMail: userEmail,
    orderDate,
    shipDate,
    distributor,
    inputPattern,
    path: uploadPath,
    apiUrl: webApiUrl,
  };
};

export const buildCreateUploadUrlRequest = ({
  distributor,
  tooType,
  fileKind,
  fileNames,
  path,
}) => {
  return {
    distributor,
    ...(tooType ? { tooType } : {}),
    fileKind,
    fileCount: fileNames.length,
    fileNames,
    ...(path ? { path } : {}),
  };
};

export const buildResetUploadPathRequest = ({ distributor, path }) => {
  return {
    distributor,
    path,
  };
};

export const uploadFileWithPresignedUrl = async ({
  uploadUrl,
  file,
  contentType = "application/pdf",
}) => {
  return axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": contentType,
    },
  });
};

export const callUploadForm = async (requestData) => {
  return postUploadApi("uploadForm", requestData);
};

export const callCreateUploadUrl = async (requestData) => {
  return postUploadApi("createUploadUrl", requestData);
};

export const callResetUploadPath = async (requestData) => {
  return postUploadApi("resetUploadPath", requestData);
};
